import _ from 'lodash';
import { createSelector } from 'reselect';
import parseAccountName from '../utils/parseAccountName';
import { ACCOUNT_TYPES } from './appConstants';
import type { IRootState } from './types/rootReducers.t';
import type { IMobileAppRootState } from '../mobileAppCore/types/mobileAppState.t';
import { IAccountCode } from './types/appReducer.t';
import { PAGES_OPEN_MOBILE_APP_IS_INVISIBLE } from "../modules/OpenMobileApp/openMobileAppConstants";
import { USER_STATUSES_NAMES } from "../modules/UserStatus/userStatusesConstants";

const moduleSelector = (state: IRootState | IMobileAppRootState) => state.app;
const routerSelector = (state: IRootState) => state.router;

export const getIsAppInitLoading = createSelector(
    moduleSelector, state => state.isInitLoading,
);

export const getAppModeSelector = createSelector(
    moduleSelector, state => state.appMode,
);

export const makeSelectAppInited = () =>
    createSelector(
        moduleSelector,
        state => state.inited,
    );

export const getAppLangSelector = createSelector(
    moduleSelector,
    state => state.lang,
);

export const makeSelectAppLoggedIn = () =>
    createSelector(
        moduleSelector,
        state => state.loggedIn,
    );

export const makeSelectAppLoggedInProcess = () =>
    createSelector(
        moduleSelector,
        state => state.loggedInProcess,
    );


export const makeSelectAppFormError = () =>
    createSelector(
        moduleSelector,
        state => state.formError,
    );

export const makeSelectPasswordResetSent = () =>
    createSelector(
        moduleSelector,
        state => state.passwordResetSent,
    );

export const getIpCountry = createSelector(
    moduleSelector, state => state.ipcountry,
);

export const getAppUser = createSelector(
    moduleSelector, state => state.user,
);

export const getAvailableCountries = createSelector(
    moduleSelector, state => _.sortBy(state.availableCountries, 'name'),
);

export const getOpenedModalSelector = createSelector(
    moduleSelector, state => state.openedModal,
);

export const getOpenedModalParamsSelector = createSelector(
    getOpenedModalSelector, state => state?.params,
);

export const makeSelectOpenedModalPrevious = () =>
    createSelector(
        moduleSelector,
        state => state.openedModalPrevious,
    );

export const getRegistrationData = createSelector(
    moduleSelector, state => state.registration,
);

export const makeSelectChangeEmail = () =>
    createSelector(
        moduleSelector,
        state => state.changeEmail,
    );

export const getCodeChecked = createSelector(
    moduleSelector, state => state.codeChecked,
);

export const getAvailableLocales = createSelector(moduleSelector, state => state?.availableLocales || []);

export const getDomainConfig = createSelector(moduleSelector, state => state?.domainConfig);

export const getPrivacyPolicyLink = createSelector(getDomainConfig, state => state.privacyPolicyLink);
export const getClientAgreementLink = createSelector(getDomainConfig, state => state.clientAgreementLink);
export const getDomainConfigCountry = createSelector(getDomainConfig, state => state?.config_country || {});
export const getReferralRulesLink = createSelector(getDomainConfig, state => state.referralRules);

export const makeSelectRoutePathname = () =>  //@ts-ignore
    createSelector(routerSelector, routerState => routerState?.location?.pathname || '');

export const getIsCheckCodeLoading = createSelector(
    moduleSelector, state => state.isCheckCodeLoading,
);

export const getPasswordMaxLengthSelector: (state: IRootState) => number =
    createSelector(moduleSelector, state => state?.passwordMaxLength || 40);

export const getPasswordMinLengthSelector =
    createSelector(moduleSelector, state => state?.passwordMinLength || 8);

export const getUserEmailSelector = createSelector(
    getAppUser, user => user?.email || '',
);

export const getUserDefaultAccountCode = createSelector(
    getAppUser, user => user?.defaultAccountCode || '',
);

export const getUserPaymentAccountCode = createSelector(
    getAppUser, user => user?.payment_account_code || '',
);

export const getUserSelectedAccountCode = createSelector(
    moduleSelector, state => state.selectedAccountCode as IAccountCode,
);

export const getUserSelectedAccount = createSelector(
    getAppUser, getUserSelectedAccountCode, (state, selectedAccountCode) => {
        return _.find(state.accounts, { name: selectedAccountCode });
    });

export const getUserSelectedAccountType = createSelector(
    getUserSelectedAccountCode, selectedAccountCode => parseAccountName(selectedAccountCode || '')?.type,
);

export const getIsUserSelectedAccountTypeLive = createSelector(
    getUserSelectedAccountType, state => state === ACCOUNT_TYPES.LIVE,
);

export const getIsUserSelectedAccountTypeDemo = createSelector(
    getUserSelectedAccountType, state => state === ACCOUNT_TYPES.DEMO,
);

export const getUserAccounts = createSelector(
    getAppUser, state => state?.accounts || [],
);

export const getIsUserBuyerSelector = createSelector(
    getAppUser, state => state.buyer,
);

export const getUserLiveAccount = createSelector(
    getUserAccounts, accounts => {
        return _.find(accounts, { type: ACCOUNT_TYPES.LIVE });
    },
);

export const getUserHasLiveAccount = createSelector(
    getUserAccounts, accounts => {
        return !!_.some(accounts, { type: ACCOUNT_TYPES.LIVE });
    },
);

export const getAbTestsSelector = createSelector(
    moduleSelector, state => state.abTests,
);

export const getAbTestsIsUsedQueueSelector = createSelector(
    moduleSelector, state => state.abTestsIsUsedQueue,
);

export const getUserUidSelector = createSelector(
    getAppUser, user => user.uid || null,
);

export const getUserEmailConfirmedSelector = createSelector(
    getAppUser, state => state.email_confirmed || false,
);

export const getIsFirstLoginSelector = createSelector(
    moduleSelector, state => state.isFirstLogin,
);

export const getOnboardingCompleted = createSelector(
    moduleSelector, state => state.onboardingCompleted,
);

export const getOnboardingCompleteLoading = createSelector(
    moduleSelector, state => state.onboardingCompleteLoading,
);

export const getAppFeatures = createSelector(
    moduleSelector, state => state.features || {},
);

export const getAppIsCreatePasswordLoading = createSelector(
    moduleSelector, state => state.isCreatePasswordLoading || false,
);

export const getAccountsTradeInfoSelector = createSelector(
    moduleSelector, state => state.accountsTradeInfo || {},
);

export const getUserRecents = createSelector(
    getAppUser, state => state?.recents || {},
);

export const getPadVersion = createSelector(
    moduleSelector, state => state.padVersion,
);

export const getIsOpenMobileAppBannerVisible = createSelector(
    [moduleSelector, makeSelectRoutePathname()], (state, pathname) => {
        return !PAGES_OPEN_MOBILE_APP_IS_INVISIBLE.includes(pathname) && state.isOpenMobileAppBannerVisible;
    },
);

export const getIsAppVisibility = createSelector(
    moduleSelector, state => state.visibility,
);

export const getAppAnnouncement = createSelector(
    moduleSelector, state => state.announcement,
);

export const getAppAskPhone = createSelector(
    moduleSelector, state => state.askPhone,
);

export const getUserLocale = createSelector(
    getAppUser,
    state => state.locale,
);

export const getWaitingWithdrawal = createSelector(getAppUser, state => state.waiting_withdrawal);

export const getRequestFeedback = createSelector(moduleSelector, state => state.requestFeedback);
export const getRequestFeedbackSent = createSelector(moduleSelector, state => state.requestFeedbackSent);
export const getRequestFeedbackError = createSelector(moduleSelector, state => state.requestFeedbackError);

export const getZendeskConfigSelector = createSelector(moduleSelector, state => state.zendesk);

export const getIsDeleteProfileLoadingSelector = createSelector(moduleSelector, state => state.isDeleteProfileLoading);
export const getDeleteProfileErrorSelector = createSelector(moduleSelector, state => state.deleteProfileError);

export const getContactsEmailSelector = createSelector(getDomainConfig, state => state.contactsEmail);

export const getUserVerify = createSelector(getAppUser, state => state.verify);

export const getIsOneClickTradingOn = createSelector(
    moduleSelector, state => state.isOneClickTradingOn,
);

export const getUserWithdrawalLimitsSelector = createSelector(getAppUser, state => state?.withdrawalLimits);

export const getIsFirstDealDoneSelector = createSelector(
    moduleSelector, state => !!state.isFirstDealDone,
);

export const getUsernameFormDataSelector = createSelector(moduleSelector, state => state.usernameChange);

export const getCurrentUsernameSelector = createSelector(getAppUser, state => state.username);

export const getIsLeaderboardOnboardingPassedSelector = createSelector(getAppUser, state => state.leaderboardOnboardingPassed);

export const getIsSkipLeaderboardOnboardingSelector = createSelector([getAppUser, getIsLeaderboardOnboardingPassedSelector], (user, isLeaderboardOnboardingPassed) => isLeaderboardOnboardingPassed && user.user_status !== USER_STATUSES_NAMES.FREE);